import { Grid, TextField, Typography } from '@mui/material';
import EntityView from 'components/entityView';
import { FinsightEntityFieldValue } from 'components/renderField';
import React, { useState } from 'react';
import { FinsightEntityField } from 'shared';

import { FieldEditorSection } from '../types/FieldEditorSection';
import { InputArea } from './InputArea';

// Creates a sample value for a field based on its type
const getSampleValue = (field: FieldEditorSection['field']): unknown => {
  switch (field.type) {
    case 'string':
      return field.name ? `Sample ${field.name}` : 'Sample text';
    case 'number':
      return 42;
    case 'boolean':
      return true;
    case 'date':
      return new Date().toISOString();
    case 'enum':
      if (Array.isArray(field.properties) && field.properties.length > 0) {
        return field.properties[0].name || 'Option 1';
      }
      return 'Option 1';
    case 'entity':
      return { id: 'sample-id', name: 'Sample Entity' };
    case 'computed':
      if (field.meta?.type === 'number') {
        return 100;
      }
      return 'Computed Value';
    case 'image':
      return 'https://via.placeholder.com/150';
    case 'file':
      return 'https://via.placeholder.com/150';
    case 'object':
      if (Array.isArray(field.properties)) {
        const obj: Record<string, unknown> = {};
        field.properties.forEach((prop) => {
          if (prop.name) {
            obj[prop.name] = getSampleValue(prop);
          }
        });
        return obj;
      }
      return { key: 'value' };
    default:
      return 'Sample value';
  }
};

export function FieldPreview({ field }: FieldEditorSection) {
  // Create a mock field with the schema for rendering
  const mockField: FinsightEntityField = {
    name: field.name || 'Sample Field',
    type: 'string', // This doesn't affect rendering
    schema: field,
    workspace_id: 'global',
  };

  // Sample value for the field
  const defaultSampleValue = getSampleValue(field);

  const [sampleValue, setSampleValue] = useState(defaultSampleValue);

  return (
    <InputArea
      name="Preview"
      description="Preview how this field will appear in different entity view types"
    >
      <TextField
        value={sampleValue}
        onChange={(e) => setSampleValue(e.target.value)}
      />
      <Grid container spacing={3} mb={8}>
        {/* Card View */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Card View
          </Typography>

          <EntityView
            disableActions
            entity={{
              type: 'ignore',
              types: ['ignore'],
              id: 'ignore',
              extraFields: {
                [mockField.name]: sampleValue,
              },
              extraFieldsMeta: {
                [mockField.name]: mockField,
              },
              name: 'Entity Name',
              description: 'Entity Description',
            }}
            viewType="card"
            hideParent
            hideType
          />
        </Grid>

        {/* List View */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            List View
          </Typography>
          <EntityView
            disableActions
            entity={{
              type: 'ignore',
              types: ['ignore'],
              id: 'ignore',
              extraFields: {
                [mockField.name]: sampleValue,
              },
              extraFieldsMeta: {
                [mockField.name]: mockField,
              },
              name: 'Entity Name',
              description: 'Entity Description',
            }}
            viewType="list"
            hideParent
            hideType
          />
        </Grid>

        {/* Entity Main */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Entity Main
          </Typography>
          <FinsightEntityFieldValue
            field={mockField}
            value={sampleValue}
            viewType="entity_main"
            editable
          />
        </Grid>

        {/* Entity Sidebar */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Entity Sidebar
          </Typography>

          <FinsightEntityFieldValue
            field={mockField}
            value={sampleValue}
            viewType="entity_sidebar"
            editable
          />
        </Grid>
      </Grid>
    </InputArea>
  );
}
